import { useMergeState } from '@/shared/lib/customHooks';
import { useRef } from 'react';
import CATEGORY from './constant';
import type { EcgViewerGuidelineProps } from './ecgViewerGuideline.types';

/**
 * Drives the ECG viewer guideline panel with its category tabs and lazily loaded guide images. Holds the Introduction, HR chart, ECG chart and Event categories in a ref, tracks the active category plus which images have loaded on each of the four pages in merged state, and exposes the back, category-switch and image-loaded handlers.
 *
 * @param handleClickBack - Callback closing the guideline and returning to the viewer.
 * @returns Guideline state, the category list and the back, category and image-load handlers.
 */
export const useEcgViewerGuideline = ({ handleClickBack }: EcgViewerGuidelineProps) => {
  const tempArr = useRef<any[]>([]);
  const categoryList = useRef([
    {
      label: CATEGORY.INTRODUCTION,
      value: CATEGORY.INTRODUCTION,
    },
    {
      label: CATEGORY.HR_CHART,
      value: CATEGORY.HR_CHART,
    },
    {
      label: CATEGORY.ECG_CHART,
      value: CATEGORY.ECG_CHART,
    },
    {
      label: CATEGORY.EVENT,
      value: CATEGORY.EVENT,
    },
  ]);

  const [state, setState] = useMergeState<any>({
    category: categoryList.current[0].value,
    loadedImgsPage1: null,
    loadedImgsPage2: [],
    loadedImgsPage3: null,
    loadedImgsPage4: [],
  });

  /**
   * Closes the ECG viewer guideline panel. Invokes the back callback supplied by the parent.
   *
   * @returns Nothing.
   */
  const onClickBack = () => {
    handleClickBack();
  };

  /**
   * Switches the guideline to the clicked category. Stores the value as state.category and ignores the name argument.
   *
   * @param name - Name of the toggle button group; unused.
   * @param value - Category value to activate.
   * @returns Nothing.
   */
  const onClickCategory = (name: any, value: any) => {
    setState({ category: value });
  };

  /**
   * Records that a guideline image finished loading so its skeleton placeholder can be dropped. Stores the image directly for page '1' and, for pages '2' to '4', appends it to a shared ref array and copies that array into the matching loadedImgsPage key.
   *
   * @param img - Source of the image that finished loading.
   * @param page - Guideline page the image belongs to, '1' through '4'.
   * @returns Nothing.
   */
  const onLoadImg = (img: any, page: any) => {
    switch (page) {
      case '1': {
        setState({ loadedImgsPage1: img });
        break;
      }
      case '2': {
        tempArr.current.push(img);
        setState({ loadedImgsPage2: [...tempArr.current] });
        break;
      }
      case '3': {
        tempArr.current.push(img);
        setState({ loadedImgsPage3: [...tempArr.current] });
        break;
      }
      case '4': {
        tempArr.current.push(img);
        setState({ loadedImgsPage4: [...tempArr.current] });
        break;
      }
      default: {
        break;
      }
    }
  };

  return { state, categoryList, onClickBack, onClickCategory, onLoadImg };
};
